import { LoginForm } from '@/components/auth/LoginForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAuthNavigation } from '@/hooks/useAuthNavigation';

const Login = () => {
  const { user, loading } = useAuth();
  const { getDashboardRouteByRole } = useAuthNavigation();
  const [searchParams] = useSearchParams();

  // Validate that a path is appropriate for the user's roles
  const canAccessPath = (path: string, roles?: string[]): boolean => {
    if (!roles || roles.length === 0) return false;
    const pathRoleMap: Record<string, string[]> = {
      '/buyer': ['buyer'],
      '/seller': ['seller'],
      '/dashboard/broker': ['broker'],
      '/dashboard/consultant': ['consultant'],
      '/dashboard/lender': ['lender'],
      '/partners': ['partner'],
      '/admin': ['admin', 'staff'],
      '/staff': ['admin', 'staff'],
    };
    for (const [prefix, allowedRoles] of Object.entries(pathRoleMap)) {
      if (path.startsWith(prefix)) {
        return roles.some(r => allowedRoles.includes(r));
      }
    }
    return true; // generic paths are accessible
  };

  // Si l'utilisateur est déjà connecté, rediriger vers son dashboard
  if (!loading && user) {
    const nextParam = searchParams.get('next');
    // Only use nextParam if user has matching role
    const validNext = nextParam && canAccessPath(nextParam, user.roles) ? nextParam : null;
    const dashboardRoute = validNext || getDashboardRouteByRole();
    console.log('✅ User already authenticated, redirecting to:', dashboardRoute);
    return <Navigate to={dashboardRoute} replace />;
  }

  // Afficher un loader pendant la vérification de l'authentification
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-4 flex flex-col items-center">
            <img src="/noBgColor.png" alt="AcquizGroup" className="h-24 w-auto mb-2" />
            <p className="text-sm text-muted-foreground">Deal Platform</p>
          </div>
          <CardTitle>Welcome back</CardTitle>
          <CardDescription>
            Sign in to your account to continue
          </CardDescription>
        </CardHeader>
        <CardContent>
          <LoginForm />
          <div className="mt-4 text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <a href="/register/brokers" className="text-primary hover:underline font-medium">
              Broker
            </a>
            {' | '}
            <a href="/register/buyers" className="text-primary hover:underline font-medium">
              Buyer
            </a>
            {' | '}
            <a href="/register/consultant" className="text-primary hover:underline font-medium">
              Consultant
            </a>
            {' | '}
            <a href="/register/lender" className="text-primary hover:underline font-medium">
              Lender
            </a>
            {' | '}
            <a href="/register/partner" className="text-primary hover:underline font-medium">
              Partner
            </a>
            {' | '}
            <a href="/register/sellers" className="text-primary hover:underline font-medium">
              Seller
            </a>
          </div>
          <div className="mt-4 text-center text-sm">
            <Link to="/admin-portal" className="text-primary hover:underline font-medium">
              Access Admin Portal
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
